// ELS MODELS CONCEPTUALS DEL SSM: la forma, les regles i la geometria.
//
// Fora de React i sense cap import, com `estocsFluxos.js`. Es prova sol:
// `npm run prova:conceptuals`.
//
// ── QUÈ ÉS UN MODEL CONCEPTUAL, I QUÈ NO ─────────────────────────────────────────────────────
//
// **No és un formulari.** És el graf de les activitats que un sistema hauria de fer per complir
// el que diu la seva definició arrel, ordenades per **dependència lògica**: què ha d'haver passat
// perquè una altra cosa pugui passar.
//
// I **no descriu com funciona el món**. És una construcció nocional, feta des d'una definició
// arrel concreta —una manera de mirar-se la situació— i serveix per **comparar-la** amb el que
// passa de debò a l'etapa 5. Confondre el model conceptual amb «com haurien de ser les coses» és
// l'errada que Checkland dibuixa amb una línia al mig del seu diagrama.
//
// ── EL SUBSISTEMA DE MONITORATGE I CONTROL ───────────────────────────────────────────────────
//
// **Sense això no és un model conceptual de Checkland**, i és el tros que sempre falta.
//
// Un sistema d'activitat humana no és només el que fa: és també **com sap que ho està fent bé i
// què fa si no**. Per això, a més de les activitats operatives, n'hi ha tres més:
//
//     definir les mesures  →  vigilar  →  prendre l'acció correctora
//
// Les mesures són **les cinc E de la definició arrel** —eficàcia, eficiència, efectivitat,
// eticitat i elegància—: no es tornen a escriure aquí, es llegeixen d'allà. Si es copiessin,
// serien dues veritats.
//
// Al dibuix, el subsistema **no s'enganxa a cada activitat operativa una per una**: es dibuixa
// una frontera al voltant de les operatives i dues fletxes, una que hi surt cap a «vigilar» i una
// que hi entra des de «prendre l'acció correctora». És com ho dibuixa Checkland i és el que fa
// llegible un model de vuit activitats.

export const MIDES = {
  activitat: { amplada: 160, alcada: 62 },
  marge: 28,          // el marge de la frontera al voltant de les operatives
};

export const OPERATIVA = 'operational';
export const MONITORATGE = 'monitoring';

// Les tres activitats del subsistema. Els identificadors són fixos perquè es puguin reconèixer
// sempre —el text el posa el diccionari, i canviar d'idioma no els ha de tornar activitats noves.
export const ACTIVITATS_MONITORATGE = [
  { id: 'mc_mesures', clau: 'cmMesures' },
  { id: 'mc_vigilar', clau: 'cmVigilar' },
  { id: 'mc_accio', clau: 'cmAccio' },
];

// La regla dels 7±2 de Checkland: un model conceptual a un sol nivell de resolució en porta unes
// set. No és cap límit —hi ha models de dotze— però passar de nou sol voler dir que s'hi han
// barrejat dos nivells, i val la pena dir-ho **una vegada i sense insistir-hi**.
export const RECOMANADES = { minim: 3, maxim: 9 };

export function nouModelConceptual(rootDefinitionId, ara = 0) {
  return {
    id: `cm_${ara}`,
    rootDefinitionId,
    activities: ACTIVITATS_MONITORATGE.map((a, i) => ({
      id: a.id, clau: a.clau, name: '', type: MONITORATGE,
      x: 560, y: 90 + i * 100,
    })),
    // El subsistema ja ve encadenat: mesures → vigilar → acció. És l'única part del model que se
    // sap d'entrada, perquè és la mateixa a tots.
    dependencies: [
      { id: 'd_mc_1', from: 'mc_mesures', to: 'mc_vigilar' },
      { id: 'd_mc_2', from: 'mc_vigilar', to: 'mc_accio' },
    ],
  };
}

export const esMonitoratge = (a) => a.type === MONITORATGE;
export const operatives = (model) => (model?.activities || []).filter((a) => !esMonitoratge(a));
export const monitoratge = (model) => (model?.activities || []).filter(esMonitoratge);

// El número que es veu a cada activitat operativa. Es calcula de la posició a la llista i **no es
// desa**: renumerar hauria de ser gratis, i un número desat es desincronitza el dia que se
// n'esborra una del mig. Per això l'etapa 5 s'ha d'aparellar per `id` i mai pel número.
export function numeroDe(model, id) {
  const i = operatives(model).findIndex((a) => a.id === id);
  return i < 0 ? null : i + 1;
}

// ── LES REGLES ───────────────────────────────────────────────────────────────────────────────

export function potDependre(model, origenId, destiId) {
  if (!origenId || !destiId) return 'ssm.cmErrNoHiEs';
  if (origenId === destiId) return 'ssm.cmErrMateixa';
  const deps = model?.dependencies || [];
  if (deps.some((d) => d.from === origenId && d.to === destiId)) return 'ssm.cmErrRepetida';
  // UN CICLE ÉS UNA CONTRADICCIÓ, no una retroacció. Aquí una fletxa vol dir «això ha d'haver
  // passat perquè allò pugui passar», i «A abans de B abans de A» no es pot complir mai. Al llenç
  // causal un cicle és justament el que s'hi busca; aquí és un error, i la diferència és que allà
  // les fletxes són influència i aquí són ordre.
  if (portaACicle(deps, origenId, destiId)) return 'ssm.cmErrCicle';
  return null;
}

function portaACicle(deps, origenId, destiId) {
  // Hi hauria cicle si des del destí ja es pot arribar a l'origen.
  const sortides = new Map();
  for (const d of deps) {
    if (!sortides.has(d.from)) sortides.set(d.from, []);
    sortides.get(d.from).push(d.to);
  }
  const vistos = new Set();
  const pila = [destiId];
  while (pila.length) {
    const n = pila.pop();
    if (n === origenId) return true;
    if (vistos.has(n)) continue;
    vistos.add(n);
    for (const seg of sortides.get(n) || []) pila.push(seg);
  }
  return false;
}

// El que val la pena dir d'un model, sense impedir res. Com les unitats del llenç d'estocs: són
// avisos, perquè un model a mig fer n'ha de tenir i no per això ha de deixar de desar-se.
export function avisos(model, rootDefinition) {
  const fora = [];
  const ops = operatives(model);
  for (const a of ops) {
    if (!String(a.name || '').trim()) fora.push({ id: a.id, clau: 'ssm.cmErrSenseNom' });
  }
  if (ops.length && ops.length < RECOMANADES.minim) fora.push({ clau: 'ssm.cmPoques' });
  if (ops.length > RECOMANADES.maxim) fora.push({ clau: 'ssm.cmMoltes', n: ops.length });
  // Les cinc E viuen a la definició arrel i el subsistema de monitoratge les llegeix d'allà. Si
  // no n'hi ha cap, el subsistema no té amb què vigilar.
  if (rootDefinition && !['efficacy', 'efficiency', 'effectiveness', 'ethicality', 'elegance']
      .some((e) => String(rootDefinition[e] || '').trim())) {
    fora.push({ clau: 'ssm.cmSenseE' });
  }
  return fora;
}

// El nom d'una activitat, partit en fins a tres línies.
//
// Els noms d'un model conceptual són **frases amb verb** —«Definir les mesures de rendiment»— i
// retallar-les amb punts suspensius fa que el model no es pugui llegir, que és justament per al
// que serveix. Es parteix per paraules i, si una línia sola no hi cap, aleshores sí que es
// retalla, però només aquella.
export function parteixNom(nom, perLinia = 20, maxLinies = 3) {
  const paraules = String(nom || '').trim().split(/\s+/).filter(Boolean);
  if (!paraules.length) return [''];
  const linies = [];
  let actual = '';
  for (const p of paraules) {
    const provant = actual ? `${actual} ${p}` : p;
    if (provant.length <= perLinia) { actual = provant; continue; }
    if (actual) linies.push(actual);
    actual = p;
    if (linies.length === maxLinies - 1) break;
  }
  if (actual && linies.length < maxLinies) linies.push(actual);
  // El que no hi ha cabut es diu amb punts suspensius a l'última línia, no s'amaga.
  const usades = linies.join(' ').split(/\s+/).length;
  if (usades < paraules.length) {
    const ultima = linies[linies.length - 1];
    linies[linies.length - 1] = `${ultima.slice(0, Math.max(1, perLinia - 1))}…`;
  }
  return linies.map((l) => (l.length > perLinia ? `${l.slice(0, perLinia - 1)}…` : l));
}

// ── GEOMETRIA ────────────────────────────────────────────────────────────────────────────────

export function ancora(a, cx, cy) {
  const { amplada, alcada } = MIDES.activitat;
  const dx = cx - a.x, dy = cy - a.y;
  const d = Math.hypot(dx, dy) || 1;
  const ux = dx / d, uy = dy / d;
  const tx = ux === 0 ? Infinity : (amplada / 2) / Math.abs(ux);
  const ty = uy === 0 ? Infinity : (alcada / 2) / Math.abs(uy);
  const t = Math.min(tx, ty);
  return { x: a.x + ux * t, y: a.y + uy * t };
}

export function geometriaDependencia(origen, desti) {
  const a = ancora(origen, desti.x, desti.y);
  const b = ancora(desti, origen.x, origen.y);
  return { a, b, angle: Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI };
}

// La frontera al voltant de les activitats operatives. És el que fa que el subsistema de
// monitoratge s'hi enganxi amb dues fletxes i no amb dues per activitat.
export function fronteraOperativa(model) {
  const ops = operatives(model);
  if (!ops.length) return null;
  const { amplada, alcada } = MIDES.activitat;
  const m = MIDES.marge;
  const x0 = Math.min(...ops.map((a) => a.x - amplada / 2)) - m;
  const y0 = Math.min(...ops.map((a) => a.y - alcada / 2)) - m;
  const x1 = Math.max(...ops.map((a) => a.x + amplada / 2)) + m;
  const y1 = Math.max(...ops.map((a) => a.y + alcada / 2)) + m;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

// Esborrar una activitat se n'endú les dependències que hi arriben o en surten. Les tres del
// subsistema **no s'esborren**: són el que fa que això sigui un model conceptual i no un diagrama
// de caixes.
export function esborraActivitat(model, id) {
  if (ACTIVITATS_MONITORATGE.some((a) => a.id === id)) return model;
  return {
    ...model,
    activities: (model.activities || []).filter((a) => a.id !== id),
    dependencies: (model.dependencies || []).filter((d) => d.from !== id && d.to !== id),
  };
}

const CONCEPTUALS = {
  MIDES, OPERATIVA, MONITORATGE, ACTIVITATS_MONITORATGE, RECOMANADES,
  nouModelConceptual, esMonitoratge, operatives, monitoratge, numeroDe, parteixNom,
  potDependre, avisos, ancora, geometriaDependencia, fronteraOperativa, esborraActivitat,
};
export default CONCEPTUALS;
